import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import {
	PurchaseOrderReceiptExceptionStatus,
	PurchaseOrderReceiptExceptionType,
} from '../../enums';
import { BaseEntity } from './base.entity';
import { PurchaseOrderReceiptDetailEntity } from './purchase-order-receipt-detail.entity';
import { UserEntity } from './user.entity';
import { VendorEntity } from './vendor.entity';

/**
 * One row per non-zero exception bucket on a PurchaseOrderReceiptDetail
 * (a single detail line can carry multiple exceptions — e.g. some
 * damaged AND some over-shipped on the same line). Drives the
 * Outstanding Vendor Issues tab on POs and the Open Issues tab on
 * Receipts. Lifecycle is captured in the
 * `purchase_order_receipt_exception_event` audit log — this row only
 * holds the current state.
 *
 * Per Phase D3 of the PO Receipts plan we deliberately do NOT
 * denormalize `resolvedDate` / `fkResolvedByUserId` here — those are
 * derivable from the event log (the event with toStatus=RESOLVED).
 * BaseEntity covers row-level audit (who created the exception, who
 * last touched it).
 *
 * `fkVendorId` is denormalized for filter performance — every PO
 * "Outstanding Vendor Issues" query joins on vendor, and walking
 * detail → receipt → vendor on every row gets expensive. The vendor
 * is always = the parent receipt's vendor (validated on insert).
 */
@Index(
	'po_receipt_exception__fk_receipt_detail__id_fk',
	['fkReceiptDetailId'],
	{},
)
@Index('po_receipt_exception__fk_vendor__id_fk', ['fkVendorId'], {})
@Index(
	'po_receipt_exception__fk_assigned_user__id_fk',
	['fkAssignedUserId'],
	{},
)
@Index('po_receipt_exception__status_idx', ['status'], {})
@Index('po_receipt_exception__type_idx', ['type'], {})
@Entity('purchase_order_receipt_exception')
export class PurchaseOrderReceiptExceptionEntity extends BaseEntity {
	@Column('int', {
		name: 'fk_purchase_order_receipt_detail__id',
		unsigned: true,
	})
	public fkReceiptDetailId!: number;

	/** Denormalized for filter speed; always = parent receipt's vendor. */
	@Column('int', { name: 'fk_vendor__id', unsigned: true })
	public fkVendorId!: number;

	/** Fixed taxonomy — drives email-template variant, dashboard groupings. */
	@Column('varchar', { name: 'type', length: 32 })
	public type!: PurchaseOrderReceiptExceptionType;

	/** Quantity affected by this exception (e.g. 5 damaged, 3 over). */
	@Column('int', { name: 'quantity', unsigned: true })
	public quantity!: number;

	/**
	 * Per-tenant configurable reason string (e.g. "Crushed packaging",
	 * "Wet pallet"). Free-form so each tenant can tune dropdowns
	 * without code changes.
	 */
	@Column('varchar', { name: 'reasonCode', length: 64, nullable: true })
	public reasonCode!: string | null;

	/** Current state. State-machine transitions enforced by the exception service. */
	@Column('varchar', { name: 'status', length: 32 })
	public status!: PurchaseOrderReceiptExceptionStatus;

	/**
	 * Whoever owns chasing the resolution. NULL = unassigned (lands
	 * in the team's open queue rather than a personal queue).
	 */
	@Column('int', {
		name: 'fk_assigned_user__id',
		unsigned: true,
		nullable: true,
	})
	public fkAssignedUserId!: number | null;

	/** Vendor's RA / credit-memo reference number (free-form). */
	@Column('varchar', { name: 'vendorClaimNumber', length: 64, nullable: true })
	public vendorClaimNumber!: string | null;

	/** What we expect to recover from vendor; populated when status flips to CREDIT_PENDING. */
	@Column('decimal', {
		name: 'expectedCreditAmount',
		precision: 15,
		scale: 2,
		nullable: true,
	})
	public expectedCreditAmount!: string | null;

	/** What was actually credited; populated when status flips to CREDIT_RECEIVED. */
	@Column('decimal', {
		name: 'actualCreditAmount',
		precision: 15,
		scale: 2,
		nullable: true,
	})
	public actualCreditAmount!: string | null;

	/** Free-form resolution notes, populated at RESOLVED / WRITTEN_OFF time. */
	@Column('text', { name: 'resolutionNotes', nullable: true })
	public resolutionNotes!: string | null;

	@ManyToOne(() => PurchaseOrderReceiptDetailEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{
			name: 'fk_purchase_order_receipt_detail__id',
			referencedColumnName: 'id',
		},
	])
	public fkReceiptDetail!: PurchaseOrderReceiptDetailEntity;

	@ManyToOne(() => VendorEntity, { onDelete: 'RESTRICT', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_vendor__id', referencedColumnName: 'id' }])
	public fkVendor!: VendorEntity;

	@ManyToOne(() => UserEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_assigned_user__id', referencedColumnName: 'id' }])
	public fkAssignedUser!: UserEntity | null;
}
